import React from 'react';
import { Shield, MapPin, Database, Share2, Trash2, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy: React.FC = () => {
  const { i18n } = useTranslation();
  const isEng = i18n.language.startsWith('en');

  const content = {
    title: isEng ? "Privacy Policy" : "Política de Privacidade",
    subtitle: isEng
      ? "How we protect your data, in compliance with Angolan laws."
      : "Como protegemos os seus dados, em conformidade com a Lei da Protecção de Dados Pessoais (Lei n.º 22/11) de Angola.",
    lastUpdated: isEng ? "Last updated: October 2024" : "Última actualização: Outubro de 2024",
    sections: [
      {
        icon: <MapPin className="text-[#FF6B35] w-6 h-6" />,
        title: isEng ? "Location Collection (Critical)" : "Recolha de Localização (Crítico)",
        desc: isEng
          ? "MotoRush collects precise GPS location data. For drivers, this data is collected even when the app is running in the background to enable trip assignments and safety tracking."
          : "A MotoRush recolhe os dados de localização GPS (coordenadas) em tempo real do seu dispositivo. Para os motoristas, a recolha é feita mesmo quando a aplicação está em segundo plano (background) ou fechada. Isto é fundamental para podermos enviar os pedidos de viagem mais próximos e garantir a segurança de todos durante a rota."
      },
      {
        icon: <Database className="text-blue-500 w-6 h-6" />,
        title: isEng ? "Personal and Financial Data" : "Dados Pessoais e Financeiros",
        desc: isEng
          ? "We store information such as your NIF, phone numbers, email addresses, and wallet transaction history to ensure legal and financial compliance."
          : "Para prestarmos o serviço, guardamos informações vitais como o seu Número de Identificação Fiscal (NIF), números de telefone, endereços de email, histórico de viagens e histórico de transações e saldos nas carteiras virtuais (wallets) da plataforma."
      },
      {
        icon: <Share2 className="text-green-500 w-6 h-6" />,
        title: isEng ? "Data Sharing" : "Partilha de Dados",
        desc: isEng
          ? "Drivers see passenger's pickup location; passengers see driver's details. Corporate accounts have access to their employees' trip history."
          : "Os seus dados são partilhados apenas para a execução do serviço: O motorista tem acesso apenas ao nome e localização actual/destino do passageiro. O passageiro tem acesso ao nome, foto, matrícula da mota e classificação do motorista. Clientes com contas Corporate (B2B) têm acesso total ao histórico de viagens, facturas e rotas efetuadas exclusivamente pelos seus próprios funcionários."
      },
      {
        icon: <Trash2 className="text-red-500 w-6 h-6" />,
        title: isEng ? "Retention and Account Deletion" : "Retenção e Eliminação de Conta",
        desc: isEng
          ? "You have the right to request account deletion directly from the app or by contacting our support. Data is retained only as required by law."
          : "Em conformidade com a APD e as exigências da Apple App Store e Google Play Store, o utilizador tem o direito de solicitar a eliminação total da sua conta e dados associados a qualquer momento, diretamente nas definições da App ou contactando o suporte. Mantemos apenas registos financeiros pelo tempo exigido pela legislação fiscal de Angola."
      },
      {
        icon: <Shield className="text-purple-500 w-6 h-6" />,
        title: isEng ? "Legal Compliance (APD)" : "Conformidade Legal (APD)",
        desc: isEng
          ? "Our processing aligns with the Angolan Data Protection Agency."
          : "A plataforma MotoRush adopta as melhores práticas de segurança da informação, alinhando as suas diretrizes operacionais às recomendações da Agência de Proteção de Dados (APD) de Angola, garantindo confidencialidade e integridade da informação."
      }
    ]
  };

  return (
    <div className="pt-24 pb-16 bg-gray-50 min-h-screen animate-fadeIn">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-orange-100 rounded-2xl mb-4 text-[#FF6B35]">
            <FileText size={32} />
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {content.title}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {content.subtitle}
          </p>
          <p className="text-sm text-gray-400 mt-4">{content.lastUpdated}</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 space-y-10">
          {content.sections.map((section, idx) => (
            <div key={idx} className="flex flex-col md:flex-row gap-6 items-start group">
              <div className="p-4 bg-gray-50 rounded-xl group-hover:bg-orange-50 transition-colors shrink-0">
                {section.icon}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {section.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
